import { Injectable, computed, signal } from '@angular/core';

export type RecentlyVisitedType =
  | 'device'
  | 'location'
  | 'partner'
  | 'partnership'
  | 'installationGroup'
  | 'installation';

export interface RecentlyVisitedEntry {
  type: RecentlyVisitedType;
  id: string;
  /** Material icon name — same icon each entity type uses in the sidebar, so a recently-used
   * row is recognizable as "a device"/"a location"/etc. at a glance. */
  icon: string;
  primary: string;
  secondary: string;
  route: string[];
  visitedAt: number;
}

const STORAGE_KEY = 'RECENTLY_VISITED';
const MAX_ENTRIES = 10;

/** Client-side only, per browser/user (epic RDT-706 explicitly rules out backend persistence
 * for this widget) — a fresh browser/device starts with an empty list, by design. */
@Injectable({ providedIn: 'root' })
export class RecentlyVisitedService {
  private readonly entries = signal<RecentlyVisitedEntry[]>(this.readFromStorage());

  readonly recent = computed(() => this.entries());

  /** Call from a detail page's constructor once the entity's display data is available.
   * Deduplicates by type+id, most-recent-first, capped at MAX_ENTRIES. Silently ignores a call
   * with a blank primary/id — a caller firing before its real data has loaded should not leave
   * an empty-looking row in the list. */
  record(entry: Omit<RecentlyVisitedEntry, 'visitedAt'>): void {
    if (!entry.primary?.trim() || !entry.id?.trim()) return;

    const next = [
      { ...entry, visitedAt: Date.now() },
      ...this.entries().filter(e => !(e.type === entry.type && e.id === entry.id)),
    ].slice(0, MAX_ENTRIES);
    this.entries.set(next);
    this.writeToStorage(next);
  }

  /** Filters out anything recorded before the blank-entry guard existed in `record()` — a stale
   * entry sitting in localStorage doesn't repair itself just because the guard was added later. */
  private readFromStorage(): RecentlyVisitedEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as RecentlyVisitedEntry[];
      const cleaned = parsed.filter(e => e.primary?.trim() && e.id?.trim() && e.icon?.trim());
      if (cleaned.length !== parsed.length) this.writeToStorage(cleaned);
      return cleaned;
    } catch {
      return [];
    }
  }

  private writeToStorage(entries: RecentlyVisitedEntry[]): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      // Storage full/unavailable (private browsing) — recently-used is a convenience
      // widget, not critical data, so fail silently rather than surface an error to the user.
    }
  }
}
